import React, { useEffect, useRef, useState } from 'react';
import {
  Apple,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Cpu,
  Download as DownloadIcon,
  HardDrive,
  Monitor,
  Play,
  Shield,
  Smartphone,
  Zap
} from 'lucide-react';

const platforms = [
  {
    name: 'Windows',
    icon: Monitor,
    logo: '/assets/Logos/windows-logo-silhouette.png',
    description: '64-bit installer',
    requirements: 'Windows 10+',
    actions: ['Download EXE'],
    href: '/apps/windows/windows.zip',
    downloadName: 'windows.zip'
  },
  {
    name: 'macOS',
    icon: Apple,
    logo: '/assets/Logos/mac-os-logo.png',
    description: 'Apple Silicon & Intel',
    requirements: 'macOS 12.0+',
    macDownloads: {
      appleSilicon: '/apps/macos/Drivault-1.0-AppleSilicon-arm64.zip',
      intel: '/apps/macos/Drivault-1.0-Intel-x86.zip'
    }
  },
  {
    name: 'Linux',
    icon: HardDrive,
    logo: '/assets/Logos/linux-platform.png',
    description: 'AppImage / DEB / Flatpak',
    requirements: 'Ubuntu, Debian, Fedora+',
    actions: ['Download AppImage'],
    href: '/apps/Linux/Linux.zip',
    downloadName: 'Linux.zip'
  },
  {
    name: 'iPhone & iPad',
    icon: Apple,
    logo: '/assets/Logos/app-store.png',
    description: 'iPhone and iPad',
    requirements: 'iOS',
    store: 'Download on App Store',
    href: 'https://apps.apple.com/in/iphone/search?term=drivault'
  },
  {
    name: 'Android',
    icon: Play,
    logo: '/assets/Logos/playstore.png',
    description: 'Phones and tablets',
    requirements: 'Android ',
    store: 'Get it on Google Play',
    href: 'https://play.google.com/store/search?q=drivault&c=apps&hl=en'
  }
];

const requirements = [
  ['Windows', Monitor, 'Windows 10 or later, 4GB RAM, 500MB disk space'],
  ['macOS', Apple, 'macOS 12 Monterey or later, Apple Silicon or Intel'],
  ['Linux', HardDrive, 'Ubuntu 22.04+, Debian 12+, Fedora 39+, or compatible'],
  ['Android', Play, 'Android 9 or later with secure lock screen enabled'],
  ['iOS', Smartphone, 'iOS 16 or later, iPhone and iPad compatible']
];

const faqs = [
  {
    question: 'How do I install Drivault?',
    answer: 'Choose your platform, download the installer, then sign in with your Drivault account to start syncing securely.'
  },
  {
    question: 'How do I update?',
    answer: 'Desktop and mobile apps notify you when updates are ready. You can also download the latest release from this page.'
  },
  {
    question: 'How do I uninstall?',
    answer: "Use your operating system's standard app removal flow. Your cloud files remain protected in Drivault unless you delete them from your account."
  },
  {
    question: 'Can I use Web version?',
    answer: 'Yes. The Web App works in modern Chrome, Edge, Firefox, and Safari browsers without installing a desktop client.'
  },
  {
    question: 'How secure is Drivault?',
    answer: 'Drivault is built around encrypted sync, privacy principles, and secure access controls for personal and team files.'
  }
];

const DownloadButton = ({ children, variant = 'primary', className = '' }) => {
  const base = 'inline-flex items-center justify-center rounded-md px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-1';
  const styles =
    variant === 'primary'
      ? 'bg-[#40E095] text-white shadow-lg shadow-[#40E095]/25 hover:bg-[#36C981] hover:shadow-xl hover:shadow-[#40E095]/30'
      : 'border-2 border-[#40E095] bg-white text-[#40E095] hover:bg-[#40E095] hover:text-white';

  return (
    <a href="#platforms" className={`${base} ${styles} ${className}`}>
      {children}
    </a>
  );
};

const Download = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [isMacDropdownOpen, setIsMacDropdownOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const macCardRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (macCardRef.current && !macCardRef.current.contains(event.target)) {
        setIsMacDropdownOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMacDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const triggerDownload = (downloadUrl, fileName = '') => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadFileWithProgress = async ({ key, url, fileName, label }) => {
    setDownloadProgress({ key, percent: 0, label });

    try {
      const response = await fetch(url);

      if (!response.ok || !response.body) {
        triggerDownload(url, fileName);
        setDownloadProgress({ key, percent: 100, label });
        setTimeout(() => setDownloadProgress(null), 900);
        return;
      }

      const totalBytes = Number(response.headers.get('content-length')) || 0;
      const reader = response.body.getReader();
      const chunks = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        chunks.push(value);
        receivedBytes += value.length;

        if (totalBytes) {
          setDownloadProgress({
            key,
            percent: Math.min(98, Math.round((receivedBytes / totalBytes) * 100)),
            label
          });
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, fileName);
      URL.revokeObjectURL(blobUrl);
      setDownloadProgress({ key, percent: 100, label });
      setTimeout(() => setDownloadProgress(null), 900);
    } catch (error) {
      triggerDownload(url, fileName);
      setDownloadProgress(null);
    }
  };

  const handleMacDownload = (downloadUrl, key, fileName, label) => {
    downloadFileWithProgress({ key, url: downloadUrl, fileName, label });
    setIsMacDropdownOpen(false);
  };

  return (
    <main className="overflow-hidden">
      <section className="relative flex items-center bg-gradient-to-br from-gray-50 via-white to-[#40E095]/10 pt-6">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#40E095]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#40E095]/5 blur-3xl" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#40E095]/10 text-[#40E095] px-4 py-2 rounded-full text-sm font-semibold">
                <Shield size={16} />
                <span>Available on Every Platform</span>
              </div>

              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Download <span className="text-[#40E095]">Drivault.</span>
                  <span className="block mt-2">Access Anywhere.</span>
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Secure cloud storage with end-to-end encryption. Sync your files across Windows, macOS, Linux, Android, iPhone and Web.
                </p>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                {['End-to-End Encryption', 'Fast Sync', 'Secure & Private'].map((pill) => (
                  <span key={pill} className="inline-flex items-center gap-2 rounded-full border border-[#40E095]/20 bg-white px-4 py-3 text-sm sm:text-base font-medium text-gray-700 shadow-sm">
                    <Check size={18} className="text-[#40E095]" />
                    {pill}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <DownloadButton className="text-lg">
                  Download Now
                  <ArrowRight className="ml-2" size={22} />
                </DownloadButton>
                <DownloadButton variant="outline" className="text-lg">
                  View Releases
                </DownloadButton>
              </div>
            </div>

            <div className="relative flex min-h-[32rem] items-center">
              <img
                src="/assets/Image.png"
                alt="Drivault desktop and mobile app preview"
                className="w-full drop-shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="platforms" className="pt-20 pb-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Choose your platform
            </h2>
            <p className="text-gray-600">Drivault is available on all major platforms.</p>
          </div>

          <div className="download-platform-grid mx-auto grid max-w-7xl gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {platforms.map((platform) => {
              const primaryAction = platform.actions?.[0] || platform.store;
              const isMacOS = platform.name === 'macOS';
              const activeProgress = downloadProgress?.key === platform.name ? downloadProgress : null;

              return (
                <article
                  key={platform.name}
                  ref={isMacOS ? macCardRef : null}
                  className={`download-platform-card group ${isMacOS ? 'download-platform-card-macos' : ''}`}
                >
                  <div className="mb-4 flex h-16 w-16 items-center justify-center">
                    <img
                      src={platform.logo}
                      alt={`${platform.name} logo`}
                      className="download-platform-logo"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-gray-950">{platform.name}</h3>
                  <p className="min-h-[3.25rem] text-sm leading-6 text-gray-600">
                    {platform.description}
                    {platform.requirements && (
                      <span className="block">{platform.requirements}</span>
                    )}
                  </p>

                  {isMacOS ? (
                    <div className="download-mac-menu-wrap mt-5">
                      <button
                        type="button"
                        className="download-platform-button"
                        disabled={Boolean(activeProgress)}
                        aria-expanded={isMacDropdownOpen}
                        aria-haspopup="menu"
                        aria-controls="macos-download-menu"
                        onClick={() => setIsMacDropdownOpen((current) => !current)}
                      >
                        {activeProgress ? (
                          <>
                            <span className="download-button-spinner" aria-hidden="true" />
                            <span>Downloading</span>
                          </>
                        ) : (
                          <span>Download DMG</span>
                        )}
                        {isMacDropdownOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>

                      <div
                        id="macos-download-menu"
                        role="menu"
                        className={`download-mac-menu ${isMacDropdownOpen ? 'download-mac-menu-open' : ''}`}
                        aria-hidden={!isMacDropdownOpen}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          className="download-mac-option"
                          tabIndex={isMacDropdownOpen ? 0 : -1}
                          onClick={() => handleMacDownload(
                            platform.macDownloads.appleSilicon,
                            platform.name,
                            'Drivault-1.0-AppleSilicon-arm64.zip',
                            'Downloading Apple Silicon'
                          )}
                        >
                          <Cpu className="download-mac-option-icon" size={20} />
                          <span>
                            <strong>Apple Silicon</strong>
                            <small>For M1, M2, M3, M4 and newer</small>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="download-mac-option"
                          tabIndex={isMacDropdownOpen ? 0 : -1}
                          onClick={() => handleMacDownload(
                            platform.macDownloads.intel,
                            platform.name,
                            'Drivault-1.0-Intel-x86.zip',
                            'Downloading Intel'
                          )}
                        >
                          <Cpu className="download-mac-option-icon" size={20} />
                          <span>
                            <strong>Intel</strong>
                            <small>For Intel-based Mac</small>
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={platform.href || '#'}
                      className="download-platform-button mt-5"
                      download={platform.downloadName}
                      onClick={(event) => {
                        if (platform.downloadName) {
                          event.preventDefault();
                          downloadFileWithProgress({
                            key: platform.name,
                            url: platform.href,
                            fileName: platform.downloadName,
                            label: `Downloading ${platform.name}`
                          });
                          return;
                        }

                        if (platform.href && platform.href !== '#') {
                          event.preventDefault();
                          setDownloadProgress({
                            key: platform.name,
                            percent: 0,
                            label: `Opening ${platform.name}`
                          });

                          window.setTimeout(() => {
                            window.location.href = platform.href;
                          }, 350);
                        }
                      }}
                    >
                      {activeProgress ? (
                        <>
                          <span className="download-button-spinner" aria-hidden="true" />
                          <span>Downloading</span>
                        </>
                      ) : (
                        primaryAction
                      )}
                    </a>
                  )}

                  {platform.note && (
                    <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
                      <Zap className="mr-1 inline text-yellow-500" size={13} />
                      {platform.note}
                    </div>
                  )}

                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pt-8 pb-20 bg-gradient-to-br from-gray-50 via-white to-[#40E095]/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm border border-gray-100 rounded-2xl shadow-xl p-6 sm:p-10">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                System <span className="text-[#40E095]">requirements</span>
              </h2>
              <p className="text-lg text-gray-600">Minimum Requirements</p>
            </div>
            <div className="space-y-4">
              {requirements.map(([name, Icon, text]) => (
                <div key={name} className="grid sm:grid-cols-[2.5rem_8rem_1fr] items-center gap-4 rounded-xl border border-gray-100 bg-white p-4">
                  <Icon className="text-[#40E095]" size={24} />
                  <strong className="text-gray-900">{name}</strong>
                  <span className="text-gray-600">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl p-8 sm:p-12 lg:p-16">
            <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#40E095]/20 blur-3xl" />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-3xl">
                <div className="bg-[#40E095] w-16 h-16 rounded-xl flex items-center justify-center shadow-lg shadow-[#40E095]/30 mb-6">
                  <Shield className="text-white" size={32} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Your files are protected.</h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Drivault keeps your vault private with strong encryption, private access controls, and infrastructure designed for secure sync across every device.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  {['256-bit Encryption', 'GDPR Ready', 'Private Cloud'].map((tag) => (
                    <span key={tag} className="rounded-full bg-[#40E095]/10 px-4 py-2 text-sm font-semibold text-gray-700">{tag}</span>
                  ))}
                </div>
              </div>
              <a href="/#security" className="inline-flex items-center justify-center rounded-md bg-[#40E095] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-[#40E095]/25 transition-all hover:-translate-y-1 hover:bg-[#36C981]">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 bg-gradient-to-br from-gray-50 via-white to-[#40E095]/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-[#40E095]">Questions</span>
            </h2>
            <p className="text-lg text-gray-600">Everything you need to know before installing Drivault.</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={faq.question} className="border-2 border-gray-100 rounded-xl bg-white overflow-hidden hover:border-[#40E095]/50 transition-all">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-gray-900 hover:text-[#40E095] transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  {faq.question}
                  <ChevronDown className={`shrink-0 transition-transform ${openFaq === index ? 'rotate-180 text-[#40E095]' : ''}`} size={20} />
                </button>
                {openFaq === index && (
                  <p className="px-6 pb-6 text-gray-600 leading-relaxed">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Ready to secure your files?</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <DownloadButton className="text-lg">
              <DownloadIcon className="mr-2" size={22} />
              Download Drivault
            </DownloadButton>
            <a href="/contact-us" className="inline-flex items-center justify-center rounded-md border-2 border-[#40E095] bg-white px-6 py-3 text-lg font-semibold text-[#40E095] transition-all hover:-translate-y-1 hover:bg-[#40E095] hover:text-white">
              Contact Support
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Download;
